import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  BalancesKpiItem,
  BalancesKpiListComponent,
} from '../../components/balances-kpi-list/balances-kpi-list.component';

import { CustomTabsComponent, CustomTabView } from '../../components/custom-tabs/custom-tabs.component';
import { DropdownModule } from 'primeng/dropdown';

import { AdminService } from '@smatchy/sdk';
import { map } from 'rxjs/operators';
import { OrganizerBalancesComponent } from '../organizer-balances/organizer-balances.component';

const CENTS_TO_EUR = 100;

const BALANCES_KPI_CONFIG: ReadonlyArray<{
  title: string;
  subtitle: string;
  valueColor: string;
  valueKey: 'totalIncomingEarnings' | 'totalAvailableToWithdraw' | 'totalPendingPayout';
}> = [
  {
    title: 'financePages.bkTotalIncoming',
    subtitle: 'financePages.bkSubIncoming',
    valueColor: '#FCA13B',
    valueKey: 'totalIncomingEarnings',
  },
  {
    title: 'financePages.bkTotalBalances',
    subtitle: 'financePages.bkSubFunds',
    valueColor: '#22AE58',
    valueKey: 'totalAvailableToWithdraw',
  },
  {
    title: 'financePages.bkTotalPending',
    subtitle: 'financePages.bkSubPending',
    valueColor: '#479E9C',
    valueKey: 'totalPendingPayout',
  },
];

export interface ProOrganizer {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  currentBalance: number | null;
  isEligibleForPayout: boolean;
  heldInEscrow: number | null;
  upcomingActivitiesCount: number;
  totalPayouts: number | null;
  lastPayoutDate?: Date | null;
  status: 'ineligible' | 'eligible-for-payout' | 'pending-eligibility' | 'revoked';
  hasStripeAccount: boolean;
}

@Component({
  selector: 'bo-balances',
  templateUrl: './balances.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, CommonModule, BalancesKpiListComponent, DropdownModule, OrganizerBalancesComponent],
})
export class BalancesComponent implements OnInit {
  #adminService = inject(AdminService);
  #translate = inject(TranslateService);
  #destroyRef = inject(DestroyRef);
  public tabs = signal<CustomTabView[]>([]);
  loading = signal(false);

  kpis = signal<BalancesKpiItem[]>(this.toKpiItems(null));
  #lastBalanceData: {
    totalIncomingEarnings: number;
    totalAvailableToWithdraw: number;
    totalPendingPayout: number;
  } | null = null;

  ngOnInit(): void {
    this.buildTabs();
    this.kpis.set(this.toKpiItems(this.#lastBalanceData));
    this.#translate.onLangChange.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.buildTabs();
      this.kpis.set(this.toKpiItems(this.#lastBalanceData));
    });

    this.loadOrganizerBalancesSummary();
  }

  private buildTabs(): void {
    this.tabs.set([
      {
        label: `${this.#translate.instant('financePages.balancesTitle')} (90)`,
        icon: 'pi pi-wallet',
        route: 'organizer-balances',
      },
      {
        label: `${this.#translate.instant('financePages.payoutTitle')} (0)`,
        icon: 'output',
        route: '/payout-request',
      },
    ]);
  }

  toKpiItems(
    data: { totalIncomingEarnings: number; totalAvailableToWithdraw: number; totalPendingPayout: number } | null,
  ): BalancesKpiItem[] {
    if (data) {
      this.#lastBalanceData = data;
    }
    return BALANCES_KPI_CONFIG.map((config) => ({
      title: this.#translate.instant(config.title),
      subtitle: this.#translate.instant(config.subtitle),
      valueColor: config.valueColor,
      value: data ? data[config.valueKey] / CENTS_TO_EUR : 0,
    }));
  }

  loadOrganizerBalancesSummary(): void {
    this.loading.set(true);
    this.#adminService
      .getOrganizerBalancesSummary()
      .pipe(
        map((res) => {
          if (res?.error) {
            console.error('Failed to load organizer balances summary:', res.error);
            return null;
          }
          return res?.value ?? null;
        }),
      )
      .subscribe({
        next: (data) => {
          this.kpis.set(this.toKpiItems(data));
          this.loading.set(false);
        },
        error: (err: unknown) => {
          console.error('Error loading organizer balances summary:', err);
          this.loading.set(false);
        },
      });
  }
}
